import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Check, Loader2, X } from 'lucide-react';
import { LoSiteTemplateThumbnail } from '@/components/lyrraone/LoSiteTemplateThumbnail';
import { LoSiteTemplateLivePreview } from '@/components/lyrraone/LoSiteTemplateLivePreview';
import type { LoSiteTemplateSummary } from '@/components/lyrraone/LoSiteTemplatePreviewModal';
import {
  applyLoSiteTemplate,
  fetchLoSite,
  fetchLoSiteTemplates,
} from '@/lib/lyrraone-hub';
import {
  getSetupQuestionLabel,
  LYRRAONE_SETUP_QUESTIONS,
  LYRRAONE_SETUP_TEMPLATE_CATEGORIES,
  setLyrraOneSetupWizardOpen,
  type LyrraOneSetupAnswers,
  type LyrraOneSetupWizardStep,
} from '@/lib/lyrraone-setup-wizard';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/stores/auth.store';
import type { UserPreferences } from '@/types';
import '@/styles/lyrraone-site-editor.css';
import '@/styles/lyrraone-setup-wizard.css';
import '@/styles/lo-site-template-live-preview.css';

interface LyrraOneSetupWizardProps {
  firstName: string;
  forceOpen?: boolean;
  onCompleted: () => void;
}

function stepLabel(step: LyrraOneSetupWizardStep): string {
  if (step === 'template') return 'Étape 1 sur 3';
  if (step === 'questions') return 'Étape 2 sur 3';
  if (step === 'recap') return 'Étape 3 sur 3';
  return '';
}

export function LyrraOneSetupWizard({ firstName, onCompleted }: LyrraOneSetupWizardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const user = useAuthStore((s) => s.user);

  const [step, setStep] = useState<LyrraOneSetupWizardStep>('template');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [templates, setTemplates] = useState<LoSiteTemplateSummary[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateCategory, setTemplateCategory] = useState<string>('Tous');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<LyrraOneSetupAnswers>({});
  const [siteName, setSiteName] = useState(firstName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const selectedTemplateIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedTemplateIdRef.current = selectedTemplateId;
  }, [selectedTemplateId]);

  const currentQuestion = LYRRAONE_SETUP_QUESTIONS[questionIndex];
  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  );

  const filteredTemplates = useMemo(() => {
    if (templateCategory === 'Tous') return templates;
    return templates.filter((tpl) => tpl.category === templateCategory);
  }, [templateCategory, templates]);

  useEffect(() => {
    setLyrraOneSetupWizardOpen(true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      setLyrraOneSetupWizardOpen(false);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTemplatesLoading(true);
    void Promise.all([fetchLoSiteTemplates(), fetchLoSite()])
      .then(([tpl, site]) => {
        if (cancelled) return;
        setTemplates(tpl);
        if (site?.name?.trim()) setSiteName(site.name.trim());
        if (tpl.length > 0) {
          setSelectedTemplateId((prev) => prev ?? tpl[0].id);
        }
      })
      .catch(() => {
        if (!cancelled) setError(t('lyrraone.setup.loadError', 'Impossible de charger les modèles.'));
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleClose = useCallback(() => {
    if (step === 'done') {
      onCompleted();
      return;
    }
    setCloseConfirmOpen(true);
  }, [onCompleted, step]);

  const confirmQuit = useCallback(() => {
    setCloseConfirmOpen(false);
    onCompleted();
  }, [onCompleted]);

  const goBack = () => {
    setError(null);
    if (step === 'questions') {
      if (questionIndex > 0) {
        setQuestionIndex((i) => i - 1);
        return;
      }
      setStep('template');
      return;
    }
    if (step === 'recap') {
      setQuestionIndex(LYRRAONE_SETUP_QUESTIONS.length - 1);
      setStep('questions');
    }
  };

  const selectTemplate = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    setError(null);
  }, []);

  const goNext = () => {
    setError(null);
    const activeTemplateId = selectedTemplateIdRef.current;
    if (step === 'template') {
      if (!activeTemplateId) {
        setError(t('lyrraone.setup.templateRequired', 'Choisissez un modèle pour continuer.'));
        return;
      }
      setStep('questions');
      setQuestionIndex(0);
      return;
    }
    if (step === 'questions') {
      const q = LYRRAONE_SETUP_QUESTIONS[questionIndex];
      if (!answers[q.id]) {
        setError(t('lyrraone.setup.answerRequired', 'Sélectionnez une réponse pour continuer.'));
        return;
      }
      if (questionIndex < LYRRAONE_SETUP_QUESTIONS.length - 1) {
        setQuestionIndex((i) => i + 1);
        return;
      }
      setStep('recap');
      return;
    }
    if (step === 'recap') {
      void finishSetup();
    }
  };

  const finishSetup = async () => {
    if (!selectedTemplateId || !user) return;
    setBusy(true);
    setError(null);
    try {
      await applyLoSiteTemplate(selectedTemplateId);
      const site = await fetchLoSite();
      if (site?.name?.trim()) setSiteName(site.name.trim());

      const profile = {
        ...answers,
        templateId: selectedTemplateId,
        completedAt: new Date().toISOString(),
      };

      await updateProfile({
        preferences: {
          ...(user.preferences ?? {}),
          lyrraoneSetupWizardCompleted: true,
          lyrraoneSetupProfile: profile,
        } as UserPreferences,
      });

      setStep('done');
    } catch {
      setError(t('lyrraone.setup.finishError', 'La finalisation a échoué. Réessayez.'));
    } finally {
      setBusy(false);
    }
  };

  const recapItems = LYRRAONE_SETUP_QUESTIONS.map((q) => {
    const optionId = answers[q.id];
    if (!optionId) return null;
    return {
      id: q.id,
      label: getSetupQuestionLabel(q.id, optionId),
    };
  }).filter(Boolean) as Array<{ id: string; label: string }>;

  const primaryLabel = step === 'recap'
    ? t('lyrraone.setup.finish', 'Terminer')
    : step === 'done'
      ? t('lyrraone.setup.letsGo', 'C\'est parti')
      : t('common.continue', 'Continuer');

  const handlePrimary = () => {
    if (step === 'done') {
      onCompleted();
      navigate('/marketing/site');
      return;
    }
    goNext();
  };

  const showBack = step === 'questions' || step === 'recap';
  const continueDisabled = busy || (step === 'template' && (templatesLoading || !selectedTemplateId));

  const renderMainContent = () => {
    if (step === 'template') {
      return (
        <>
          <h2 id="lo-setup-wizard-title" className="lo-setup-wizard__title">
            {t('lyrraone.setup.templateTitle', 'Choisissez un modèle de site')}
          </h2>
          <p className="lo-setup-wizard__subtitle">
            {t(
              'lyrraone.setup.templateSubtitle',
              'Sélectionnez un design professionnel — vous pourrez le personnaliser à tout moment.',
            )}
          </p>
          <div className="lo-setup-wizard__body">
            {templatesLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />
                {t('common.loading', 'Chargement…')}
              </div>
            ) : (
              <>
                <div className="lo-setup-wizard__template-filters" role="tablist" aria-label="Filtrer par métier">
                  {LYRRAONE_SETUP_TEMPLATE_CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      role="tab"
                      aria-selected={templateCategory === cat}
                      className={`lo-setup-wizard__template-filter${templateCategory === cat ? ' is-active' : ''}`}
                      onClick={() => setTemplateCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                {filteredTemplates.length === 0 ? (
                  <p className="py-8 text-center text-sm text-gray-500">
                    {t('lyrraone.setup.noTemplates', 'Aucun modèle dans cette catégorie.')}
                  </p>
                ) : (
                  <div className="lo-setup-wizard__template-grid">
                    {filteredTemplates.map((tpl) => (
                      <div
                        key={tpl.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedTemplateId === tpl.id}
                        className={`lo-setup-wizard__template-card${selectedTemplateId === tpl.id ? ' lo-setup-wizard__template-card--selected' : ''}`}
                        onClick={() => selectTemplate(tpl.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            selectTemplate(tpl.id);
                          }
                        }}
                      >
                        <div className="lo-setup-wizard__template-preview">
                          <LoSiteTemplateThumbnail
                            homePreview={tpl.homePreview}
                            preview={tpl.preview}
                            name={tpl.name}
                            siteTheme={tpl.themeId}
                            compact
                          />
                        </div>
                        <span className="lo-setup-wizard__template-meta">
                          <span className="lo-setup-wizard__template-name">{tpl.name}</span>
                          <span className="lo-setup-wizard__template-category">{tpl.category}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {selectedTemplate && !templatesLoading ? (
                  <div className="lo-setup-wizard__template-cta">
                    <button
                      type="button"
                      className="lo-setup-wizard__btn lo-setup-wizard__btn--primary lo-setup-wizard__btn--block"
                      onClick={handlePrimary}
                      disabled={continueDisabled}
                    >
                      {t('lyrraone.setup.continueWithTemplate', {
                        name: selectedTemplate.name,
                        defaultValue: `Continuer avec « ${selectedTemplate.name} »`,
                      })}
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </>
      );
    }

    if (step === 'questions' && currentQuestion) {
      return (
        <>
          <h2 id="lo-setup-wizard-title" className="lo-setup-wizard__title">
            {currentQuestion.title}
          </h2>
          {currentQuestion.subtitle ? (
            <p className="lo-setup-wizard__subtitle">{currentQuestion.subtitle}</p>
          ) : (
            <p className="lo-setup-wizard__subtitle">
              {t('lyrraone.setup.questionProgress', {
                current: questionIndex + 1,
                total: LYRRAONE_SETUP_QUESTIONS.length,
                defaultValue: `Question ${questionIndex + 1} sur ${LYRRAONE_SETUP_QUESTIONS.length}`,
              })}
            </p>
          )}
          <div className="lo-setup-wizard__body">
            <div className="lo-setup-wizard__choices" role="radiogroup" aria-labelledby="lo-setup-wizard-title">
              {currentQuestion.options.map((option) => {
                const selected = answers[currentQuestion.id] === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`lo-setup-wizard__choice${selected ? ' lo-setup-wizard__choice--selected' : ''}`}
                    onClick={() => setAnswers((prev) => ({ ...prev, [currentQuestion.id]: option.id }))}
                  >
                    <span className="lo-setup-wizard__choice-radio" aria-hidden />
                    <span className="lo-setup-wizard__choice-label">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      );
    }

    if (step === 'recap') {
      return (
        <>
          <h2 id="lo-setup-wizard-title" className="lo-setup-wizard__title">
            {t('lyrraone.setup.recapTitle', 'Partagez votre expérience')}
          </h2>
          <p className="lo-setup-wizard__subtitle">
            {t('lyrraone.setup.recapSubtitle', 'Récapitulatif de vos réponses.')}
          </p>
          <div className="lo-setup-wizard__body">
            <ul className="lo-setup-wizard__recap-list">
              {recapItems.map((item) => (
                <li key={item.id} className="lo-setup-wizard__recap-item">
                  <span className="lo-setup-wizard__recap-check" aria-hidden>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {item.label}
                </li>
              ))}
              {selectedTemplate ? (
                <li className="lo-setup-wizard__recap-item">
                  <span className="lo-setup-wizard__recap-check" aria-hidden>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {t('lyrraone.setup.recapTemplate', {
                    name: selectedTemplate.name,
                    defaultValue: `Modèle « ${selectedTemplate.name} »`,
                  })}
                </li>
              ) : null}
            </ul>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="lo-setup-wizard__done-badge">
          <span className="lo-setup-wizard__done-badge-icon" aria-hidden>
            <Check className="h-4 w-4" />
          </span>
          {t('lyrraone.setup.doneBadge', 'Étapes terminées.')}
        </div>
        <h2 id="lo-setup-wizard-title" className="lo-setup-wizard__title">
          {t('lyrraone.setup.doneTitle', {
            name: siteName,
            defaultValue: `${siteName} est prêt`,
          })}
        </h2>
        <p className="lo-setup-wizard__subtitle">
          {t(
            'lyrraone.setup.doneSubtitle',
            'Commencez par créer votre première formation ou personnaliser votre site.',
          )}
        </p>
      </>
    );
  };

  const renderVisual = () => {
    if (step === 'done' && selectedTemplate) {
      return (
        <div className="lo-setup-wizard__visual-scroll">
          <div className="lo-setup-wizard__visual-frame">
            <div className="lo-setup-wizard__visual-hero">
              <LoSiteTemplateThumbnail
                homePreview={selectedTemplate.homePreview}
                preview={selectedTemplate.preview}
                name={selectedTemplate.name}
                siteTheme={selectedTemplate.themeId}
              />
            </div>
            <div className="lo-setup-wizard__visual-device">
              <div className="lo-setup-wizard__visual-device-screen">
                <LoSiteTemplateThumbnail
                  homePreview={selectedTemplate.homePreview}
                  preview={selectedTemplate.preview}
                  name={selectedTemplate.name}
                  siteTheme={selectedTemplate.themeId}
                  compact
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (selectedTemplate) {
      return (
        <div className="lo-setup-wizard__visual-scroll">
          <LoSiteTemplateLivePreview template={selectedTemplate} />
        </div>
      );
    }

    return (
      <div className="lo-setup-wizard__visual-scroll lo-setup-wizard__visual-scroll--empty">
        <div className="lo-setup-wizard__visual-empty" aria-hidden>
          <div className="lo-setup-wizard__visual-empty-browser">
            <div className="lo-setup-wizard__visual-empty-bar" />
            <div className="lo-setup-wizard__visual-empty-body" />
          </div>
        </div>
        <p className="lo-setup-wizard__visual-empty-text">
          {t('lyrraone.setup.selectTemplatePreview', 'Sélectionnez un modèle pour voir l\'aperçu.')}
        </p>
      </div>
    );
  };

  return createPortal(
    <>
      <div className="lo-setup-wizard-overlay" role="presentation">
      <div
        className="lo-setup-wizard"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lo-setup-wizard-title"
      >
        <button
          type="button"
          className="lo-setup-wizard__close"
          onClick={handleClose}
          aria-label={t('common.close', 'Fermer')}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="lo-setup-wizard__body-grid">
          <div className="lo-setup-wizard__panel lo-setup-wizard__panel--main">
            {step !== 'done' ? (
              <div className="lo-setup-wizard__kicker">
                <span className="lo-setup-wizard__kicker-mark" aria-hidden>◆</span>
                {stepLabel(step)}
              </div>
            ) : null}

            <div className="lo-setup-wizard__main-scroll">
              {renderMainContent()}
            </div>
          </div>

          <div
            className="lo-setup-wizard__panel lo-setup-wizard__panel--visual"
            aria-hidden={step === 'questions'}
          >
            {renderVisual()}
          </div>
        </div>

        <div className="lo-setup-wizard__actions">
          <div className="lo-setup-wizard__actions-start">
            {showBack ? (
              <button type="button" className="lo-setup-wizard__btn lo-setup-wizard__btn--ghost" onClick={goBack}>
                <ArrowLeft className="h-4 w-4" aria-hidden />
                {t('common.back', 'Retour')}
              </button>
            ) : null}
          </div>

          <div className="lo-setup-wizard__actions-center">
            {error ? <p className="lo-setup-wizard__error" role="alert">{error}</p> : null}
            {step === 'template' && selectedTemplate ? (
              <p className="lo-setup-wizard__footer-hint">
                {t('lyrraone.setup.selectedTemplate', {
                  name: selectedTemplate.name,
                  defaultValue: `Modèle sélectionné : ${selectedTemplate.name}`,
                })}
              </p>
            ) : null}
          </div>

          <div className="lo-setup-wizard__actions-end">
            <button
              type="button"
              className="lo-setup-wizard__btn lo-setup-wizard__btn--primary lo-setup-wizard__btn--prominent"
              onClick={handlePrimary}
              disabled={continueDisabled}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {primaryLabel}
              {step !== 'done' && step !== 'recap' ? <ArrowRight className="h-4 w-4" aria-hidden /> : null}
            </button>
          </div>
        </div>
      </div>
      </div>

      <ConfirmDialog
        open={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        onConfirm={confirmQuit}
        title={t('lyrraone.setup.closeConfirmTitle', 'Quitter la configuration ?')}
        message={t(
          'lyrraone.setup.closeConfirm',
          'Vous pourrez reprendre plus tard depuis le menu utilisateur (Relancer l’assistant de paramétrage).',
        )}
        variant="warning"
        confirmLabel={t('lyrraone.setup.closeConfirmQuit', 'Quitter')}
        cancelLabel={t('lyrraone.setup.closeConfirmStay', 'Continuer la configuration')}
        overlayClassName="z-[130]"
      />
    </>,
    document.body,
  );
}
